// ============================================================================
// PORTAL PROCESSOS REPOSITORY
// ARQUIVO: src/services/PortalProcessosRepository.js
// RESPONSABILIDADE: Operações Firestore do PortalProcessos (autos_workspaces, notes).
// 'matters' removido no MC36 (Fase H, fusão Processos/Caderno) — nunca teve dado.
// Criado: Fase 6.3 — mover Firestore direto de PortalProcessos.jsx para repositório.
// ============================================================================

import { collection, getDocs, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { FS } from '../shared/constants/firestorePaths';

const APP_ID = FIREBASE_CONFIG?.projectId || 'portalrda-f312d';

const toMillis = (raw) => {
  try {
    if (raw?.toDate) return raw.toDate().getTime();
    const dt = new Date(raw || 0);
    return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
  } catch {
    return 0;
  }
};

const resolveCnj = (row) => {
  return (
    String(row?.numeroCNJ || '').trim() ||
    String(row?.cnj || '').trim() ||
    String(row?.numeroProcesso || '').trim() ||
    String(row?.processNumber || '').trim() ||
    ''
  );
};

/** Busca processos do cliente.
 * Fusão Processos/Caderno (MC30): a fonte 'processes' (kind: 'processes')
 * aponta para autos_workspaces (processes está vazia, confirmado na
 * auditoria) e usa partiesRefIds em vez de clientRef/client, que não têm
 * equivalente no novo schema.
 * Fusão Processos/Caderno (MC36, Fase H): fontes 'matters' removidas —
 * ocupavam a prioridade 1 ("Base Canônica") desde sempre sem nunca ter tido
 * nenhum documento (confirmado na auditoria: matters = 0 docs em produção,
 * e nenhum código no projeto escreve nessa coleção). autos_workspaces é a
 * única fonte real agora. */
export async function fetchClientProcesses(clientId) {
  const sources = [];
  if (String(APP_ID || '').trim()) {
    sources.push({ key: 'artifacts-processes', kind: 'processes', ref: collection(db, ...FS.autosWorkspaces(APP_ID)), priority: 1, label: 'Processos e Casos' });
  }

  const merged = new Map();

  const upsert = (source, row) => {
    const cnj = String(resolveCnj(row) || '').trim().toLowerCase();
    const fallbackId = String(row?.id || '').trim();
    const identity = cnj ? `cnj:${cnj}` : `${source.key}:${fallbackId}`;
    const prev = merged.get(identity);
    const payload = {
      ...row,
      __sourceKind: source.kind,
      __sourceLabel: source.label,
      __sourcePriority: source.priority,
    };
    if (!prev || source.priority < Number(prev.__sourcePriority || 999)) {
      merged.set(identity, payload);
    }
  };

  for (const source of sources) {
    const tasks = [];
    if (clientId) {
      // kind === 'processes' (autos_workspaces, MC30). Único kind desde o
      // MC36 — o ramo 'matters' foi removido, não só desviado.
      tasks.push(getDocs(query(source.ref, where('clientId', '==', clientId), limit(50))));
      tasks.push(getDocs(query(source.ref, where('partiesRefIds', 'array-contains', clientId), limit(50))));
    }
    const results = await Promise.allSettled(tasks);
    results.forEach((result) => {
      if (result.status !== 'fulfilled') return;
      result.value.docs.forEach((d) => {
        upsert(source, { id: d.id, ...(d.data() || {}) });
      });
    });
  }

  return Array.from(merged.values())
    .slice()
    .sort((a, b) => {
      const av = Math.max(toMillis(a?.updatedAt), toMillis(a?.createdAt));
      const bv = Math.max(toMillis(b?.updatedAt), toMillis(b?.createdAt));
      return bv - av;
    });
}

/** Observa notas recentes do cliente. */
export function watchClientRecentNotes(clientId, onData, onError) {
  const appId = String(FIREBASE_CONFIG?.projectId || '').trim();
  const colRef = appId
    ? collection(db, ...FS.clientNotes(appId, clientId))
    : collection(db, 'clients', clientId, 'notes');
  return onSnapshot(
    query(colRef, orderBy('createdAt', 'desc'), limit(20)),
    onData,
    onError
  );
}
